import { Total } from "@pdfking/math"

import type { FileCtx } from "#public"

// Which schema fields render, for whom, and how wide their column is.

const HIDDEN_FIELDS = new Set(["filename", "label", "sheet_depth"])

const NON_STAFF_FIELDS = new Set(["file_key", "file_id", "file_destination", "file_scale"])

// A line of inline fields is one CSS grid of equal tracks, one per share, and
// each field spans its weight in shares (default 2): lines with the same total
// line up column for column, and the fields always fill the line edge to edge.
const WEIGHTS: Record<string, number> = {
  repetitions: 1,
  subjects: 1,
  // a line holds six shares, its first field two, under grow_kind
  machine_size: 2,
  file_destination: 1,
  job_destination: 1,
  file_range: 1,
  quantity: 1,
  copies: 1,
  size_depth: 1,
  margins: 1,
  padding: 1,
  security_margins: 1,
  // Next to grow_kind and copies, over the rest of the line below it.
  material: 3,
}

// Inline fields that start a fresh line instead of joining the previous run.
const LINE_START_KEYS = new Set(["filling"])

export const starts_line = (key: string): boolean => LINE_START_KEYS.has(key)

// Vertical stack of a form's lines (inline-field grids + full-width blocks).
export const FORM_STACK = "flex flex-col gap-3"

// A full-width field owns its own line.
export const FULL_COL = "w-full"

export const is_visible = (field: string, ctx: FileCtx): boolean => {
  if (HIDDEN_FIELDS.has(field)) return false
  if (ctx.hidden_fields.includes(field)) return false
  return ctx.staff || NON_STAFF_FIELDS.has(field)
}

const weight = (key: string): number => WEIGHTS[key] ?? 2

// Literal class names, so tailwind sees every span it has to generate.
const SPANS: Record<number, string> = {
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
}

// The cell class of a field in a line grid.
export const col_span = (key: string): string => SPANS[weight(key)]!

// The template of a line grid holding `keys`: one `minmax(0, 1fr)` track per
// share, so a wide cell can't push its column past its share.
export const line_columns = (keys: string[]) => ({
  gridTemplateColumns: `repeat(${Total(...keys.map(weight))}, minmax(0, 1fr))`,
})
